import { useState } from "react";
import { CheckCircle, X, ArrowRight, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoModal from "@/components/DemoModal";
import AuthModal from "@/components/AuthModal";

const plans = [
  {
    name: "Free Trial",
    price: "Free",
    period: "for 3 months",
    tagline: "Everything you need to get started",
    highlighted: false,
    included: [
      "Up to 50 players",
      "Unlimited training session logs",
      "Attendance tracking with QR codes",
      "Player performance dashboard",
      "Basic progress reports",
      "Mobile coach app",
      "Email support",
    ],
    excluded: [
      "Video analysis",
      "Advanced analytics",
      "Parent portal",
      "Custom branding",
    ],
    cta: "Start Free Trial",
    ctaType: "trial",
  },
  {
    name: "Pay As You Go",
    price: "€49",
    period: "/ month after trial",
    tagline: "Scale with your academy",
    highlighted: true,
    included: [
      "Unlimited players",
      "All Free Trial features",
      "Video analysis & tagging",
      "Advanced analytics & exports",
      "Parent portal & notifications",
      "Custom branded reports",
      "Priority support (24hr response)",
      "Multi-coach management",
      "API access",
    ],
    excluded: [],
    cta: "Start Free Trial",
    ctaType: "trial",
  },
  {
    name: "Academy Pro",
    price: "Custom",
    period: "bespoke pricing",
    tagline: "For large multi-team academies",
    highlighted: false,
    included: [
      "Everything in Pay As You Go",
      "Unlimited teams and academies",
      "Dedicated account manager",
      "Custom integrations",
      "On-site training & onboarding",
      "SLA guarantee (99.9% uptime)",
      "Custom data retention policies",
      "White-labelling option",
    ],
    excluded: [],
    cta: "Contact Sales",
    ctaType: "demo",
  },
];

const faqs = [
  {
    q: "Do I need a credit card to start the free trial?",
    a: "No. Your 3-month free trial requires no credit card. You only pay if you decide to continue after the trial period ends.",
  },
  {
    q: "What happens after the 3-month free trial?",
    a: "After your free trial, you can continue on our Pay As You Go plan at €49/month. There's no long-term contract — cancel any time.",
  },
  {
    q: "Can I migrate my data if I switch plans?",
    a: "All your data is seamlessly transferred between plans. Nothing gets lost when you upgrade or downgrade.",
  },
  {
    q: "Is there a player limit on the paid plan?",
    a: "The Pay As You Go plan supports unlimited players. The free trial supports up to 50 players.",
  },
  {
    q: "Can I add multiple coaches to one account?",
    a: "Yes. You can invite multiple coaches and assign role-based permissions on all paid plans.",
  },
];

export default function PricingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen">
      <div className="bg-gray-900 pt-32 pb-20 text-center" data-testid="pricing-page-header">
        <div className="max-w-4xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-forest/20 border border-green-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-forest/70 text-xs font-semibold uppercase tracking-wide">
              Free for 3 Months
            </span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Simple, Transparent Pricing
          </h1>
          <p className="text-gray-400 text-lg">
            Start for free. Scale as you grow. No hidden fees, no lock-in contracts.
          </p>
        </div>
      </div>

      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 items-start" data-testid="pricing-cards">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-sm p-8 border-2 bg-white transition-all ${
                  plan.highlighted
                    ? "border-forest shadow-xl shadow-green-600/10 md:-mt-4"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`pricing-plan-${plan.name.toLowerCase().replace(/ /g, "-")}`}
              >
                {plan.highlighted && (
                  <div className="bg-forest text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3
                  className="text-xl font-black text-gray-900 mb-1"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-500 mb-6">{plan.tagline}</p>
                <div className="mb-6">
                  <span
                    className="text-4xl font-black text-gray-900"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {plan.price}
                  </span>
                  <span className="text-gray-500 text-sm ml-2">{plan.period}</span>
                </div>

                <Button
                  onClick={() =>
                    plan.ctaType === "demo" ? setDemoOpen(true) : setAuthOpen(true)
                  }
                  data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/ /g, "-")}`}
                  className={`w-full rounded-sm mb-8 ${
                    plan.highlighted
                      ? "bg-forest hover:bg-forest/90 text-white"
                      : "bg-gray-900 hover:bg-gray-800 text-white"
                  }`}
                >
                  {plan.cta} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <ul className="space-y-3">
                  {plan.included.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-forest flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                  {plan.excluded.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-400">
                      <X className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            All plans include SSL encryption, daily backups, and GDPR compliance. No credit card
            required for free trial.
          </p>
        </div>
      </div>

      <div className="py-24 bg-white" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-4xl font-black text-gray-900 mb-12 text-center"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-sm overflow-hidden"
                data-testid={`faq-item-${i + 1}`}
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-toggle-${i + 1}`}
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                  <HelpCircle
                    className={`w-5 h-5 flex-shrink-0 transition-colors ${
                      openFaq === i ? "text-forest" : "text-gray-400"
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-20 bg-forest text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-4xl font-black text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Start free for 3 months
          </h2>
          <p className="text-green-100 mb-8">
            No credit card required. Cancel any time. Full features included.
          </p>
          <Button
            onClick={() => setAuthOpen(true)}
            data-testid="pricing-bottom-cta-btn"
            className="bg-white text-green-700 hover:bg-gray-50 px-8 py-3 h-auto font-bold rounded-full"
          >
            Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab="register" />
    </div>
  );
}
